import { useEffect } from "react";
import { useLocation } from "react-router";
import { business } from "../../config/business";
import type { SiteLanguage } from "../../i18n/routes";

type PageMetaEnv = { VITE_SITE_URL?: string };

function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let link = document.querySelector<HTMLLinkElement>(selector);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    if (hreflang) link.hreflang = hreflang;
    document.head.appendChild(link);
  }
  link.href = href;
}

function setMeta(selector: string, attribute: "name" | "property", key: string, content: string) {
  let meta = document.querySelector<HTMLMetaElement>(selector);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attribute, key);
    document.head.appendChild(meta);
  }
  meta.content = content;
}

export function PageMeta({ title, description, path = "/", language = "en", alternatePath }: { title: string; description: string; path?: string; language?: SiteLanguage; alternatePath?: string }) {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const target = hash ? document.getElementById(hash.slice(1)) : null;
    if (target) target.scrollIntoView({ behavior: "instant" });
    else window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "fa" ? "rtl" : "ltr";
    document.title = title;
    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:locale"]', "property", "og:locale", language === "fa" ? "fa_IR" : "en_US");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);

    const env = (import.meta as ImportMeta & { env: PageMetaEnv }).env;
    const siteUrl = (env.VITE_SITE_URL ?? business.canonicalUrl.value).replace(/\/$/, "");
    if (!siteUrl) return;
    const canonicalUrl = `${siteUrl}${path}`;
    setLink("canonical", canonicalUrl);
    setMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => link.remove());
    if (alternatePath) {
      const englishPath = language === "en" ? path : alternatePath;
      const persianPath = language === "fa" ? path : alternatePath;
      setLink("alternate", `${siteUrl}${englishPath}`, "en");
      setLink("alternate", `${siteUrl}${persianPath}`, "fa");
      setLink("alternate", `${siteUrl}${englishPath}`, "x-default");
    }
  }, [alternatePath, description, language, path, title]);

  return null;
}
